import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDriverStore } from '../../store/driverStore';
import { useAlertManager } from '../../components/AlertProvider';
import { apiService } from '../../services/api';
import { MaterialCommunityIcons } from '../../utils/icons';
import { Colors } from '../../theme/colors';
import './VehiclesScreen.css';

interface VehicleType {
  id: number;
  name: string;
  description?: string;
}

interface VehicleFormData {
  model: string;
  plate_number: string;
  year: number;
  color: string;
  seat_count: number;
  vehicle_type_ids: number[];
}

const VehiclesScreen: React.FC = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlertManager();
  const { vehicles, getVehicles, addVehicle, updateVehicle, deleteVehicle } = useDriverStore();
  const [loading, setLoading] = useState(true);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<any>(null);
  const [formData, setFormData] = useState<VehicleFormData>({
    model: '',
    plate_number: '',
    year: new Date().getFullYear(),
    color: 'Black',
    seat_count: 4,
    vehicle_type_ids: [],
  });

  useEffect(() => {
    loadVehicles();
    loadVehicleTypes();
  }, []);

  const loadVehicles = async () => {
    setLoading(true);
    await getVehicles();
    setLoading(false);
  };

  const loadVehicleTypes = async () => {
    try {
      const response: any = await apiService.get('/vehicle-types');
      setVehicleTypes(response.data || response || []);
    } catch (error) {
      console.error('Failed to load vehicle types:', error);
    }
  };

  const handleAdd = () => {
    setEditingVehicle(null);
    setFormData({
      model: '',
      plate_number: '',
      year: new Date().getFullYear(),
      color: 'Black',
      seat_count: 4,
      vehicle_type_ids: [],
    });
    setShowModal(true);
  };

  const handleEdit = (vehicle: any) => {
    setEditingVehicle(vehicle);
    setFormData({
      model: vehicle.model || '',
      plate_number: vehicle.plate_number || '',
      year: vehicle.year || new Date().getFullYear(),
      color: vehicle.color || 'Black',
      seat_count: vehicle.seat_count || 4,
      vehicle_type_ids: vehicle.vehicle_type_ids || (vehicle.vehicle_type_id ? [Number(vehicle.vehicle_type_id)] : []) || [],
    });
    setShowModal(true);
  };

  const handleDelete = async (vehicleId: string) => {
    showAlert(
      'Delete Vehicle',
      'Are you sure you want to delete this vehicle?',
      [
        {
          text: 'Cancel',
          style: 'cancel',
        },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteVehicle(vehicleId);
              showAlert('Success', 'Vehicle deleted successfully', [{ text: 'OK' }]);
              loadVehicles();
            } catch (error: any) {
              showAlert('Error', error.message || 'Failed to delete vehicle', [{ text: 'OK' }]);
            }
          },
        },
      ]
    );
  };

  const handleToggleCategory = (typeId: number) => {
    setFormData((prev) => {
      const currentIds = prev.vehicle_type_ids || [];
      if (currentIds.includes(typeId)) {
        return {
          ...prev,
          vehicle_type_ids: currentIds.filter((id) => id !== typeId),
        };
      } else {
        return {
          ...prev,
          vehicle_type_ids: [...currentIds, typeId],
        };
      }
    });
  };

  const handleSubmit = async () => {
    if (!formData.model || !formData.plate_number) {
      showAlert('Error', 'Please fill in all required fields', [{ text: 'OK' }]);
      return;
    }

    if (formData.vehicle_type_ids.length === 0) {
      showAlert('Error', 'Please select at least one vehicle category', [{ text: 'OK' }]);
      return;
    }

    try {

      const { vehicle_type_ids, ...restFormData } = formData;
      const backendData = {
        ...restFormData,
        vehicle_type_id: vehicle_type_ids[0], 
      };

      if (editingVehicle) {
        await updateVehicle(editingVehicle.id, backendData);
        showAlert('Success', 'Vehicle updated successfully', [{ text: 'OK' }]);
      } else {
        await addVehicle(backendData);
        showAlert('Success', 'Vehicle added successfully', [{ text: 'OK' }]);
      }
      setShowModal(false);
      loadVehicles();
    } catch (error: any) {
      showAlert('Error', error.message || 'Failed to save vehicle', [{ text: 'OK' }]);
    }
  };

  const toggleActive = async (vehicle: any) => {
    try {

      const { vehicle_type_ids, ...vehicleData } = vehicle;
      await updateVehicle(vehicle.id, {
        ...vehicleData,
        vehicle_type_id: vehicle.vehicle_type_id || (vehicle_type_ids && vehicle_type_ids[0]),
        is_active: !vehicle.is_active,
      });
      loadVehicles();
    } catch (error: any) {
      showAlert('Error', error.message || 'Failed to update vehicle status', [{ text: 'OK' }]);
    }
  };

  if (loading) {
    return <div className="vehicles-loading"><div className="loading-spinner"></div></div>;
  }

  return (
    <div className="vehicles-container">
      <div className="vehicles-header">
        <button onClick={() => navigate('/')}>
          <MaterialCommunityIcons name="arrow-back" size={24} color={Colors.textPrimary} />
        </button>
        <h1>Your Vehicles</h1>
      </div>
      <div className="vehicles-content">
        {vehicles.map((vehicle) => (
          <div key={vehicle.id} className="vehicle-card">
            <div className="vehicle-card-header">
              <div className="vehicle-info">
                <h3>{vehicle.model}</h3>
                <p className="vehicle-plate">{vehicle.plate_number}</p>
                <p className="vehicle-details">{vehicle.color} - {vehicle.year}</p>
                {vehicle.vehicle_type_ids && vehicle.vehicle_type_ids.length > 0 && (
                  <div className="vehicle-categories">
                    {vehicle.vehicle_type_ids.map((typeId: number) => {
                      const type = vehicleTypes.find((t) => t.id === typeId);
                      return type ? (
                        <span key={typeId} className="category-badge">
                          {type.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                )}
                {(!vehicle.vehicle_type_ids || vehicle.vehicle_type_ids.length === 0) && vehicle.vehicle_type_id && (
                  <div className="vehicle-categories">
                    {(() => {
                      const type = vehicleTypes.find((t) => t.id === Number(vehicle.vehicle_type_id));
                      return type ? (
                        <span className="category-badge">
                          {type.name}
                        </span>
                      ) : null;
                    })()}
                  </div>
                )}
              </div>
              <div className="vehicle-actions">
                <button
                  className={`status-toggle ${vehicle.is_active ? 'active' : 'inactive'}`}
                  onClick={() => toggleActive(vehicle)}
                >
                  {vehicle.is_active ? 'Active' : 'Inactive'}
                </button>
              </div>
            </div>
            <div className="vehicle-card-actions">
              <button className="edit-button" onClick={() => handleEdit(vehicle)}>
                <MaterialCommunityIcons name="pencil" size={18} color={Colors.primary} />
                <span>Edit</span>
              </button>
              <button className="delete-button" onClick={() => handleDelete(vehicle.id)}>
                <MaterialCommunityIcons name="delete" size={18} color={Colors.error} />
                <span>Delete</span>
              </button>
            </div>
          </div>
        ))}
        {vehicles.length === 0 && (
          <div className="empty-state">
            <MaterialCommunityIcons name="car" size={80} color="#ccc" />
            <p>No vehicles added</p>
          </div>
        )}
      </div>
      <button className="add-vehicle-btn" onClick={handleAdd}>
        <MaterialCommunityIcons name="car" size={24} color="white" />
        Add Vehicle
      </button>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingVehicle ? 'Edit Vehicle' : 'Add Vehicle'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <MaterialCommunityIcons name="close" size={24} color={Colors.textPrimary} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Model *</label>
                <input
                  type="text"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  placeholder="e.g., e39"
                />
              </div>
              <div className="form-group">
                <label>Plate Number *</label>
                <input
                  type="text"
                  value={formData.plate_number}
                  onChange={(e) => setFormData({ ...formData, plate_number: e.target.value })}
                  placeholder="e.g., 77AA777"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Year</label>
                  <input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) || new Date().getFullYear() })}
                    min="1900"
                    max={new Date().getFullYear() + 1}
                  />
                </div>
                <div className="form-group">
                  <label>Color</label>
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    placeholder="e.g., Black"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Seat Count</label>
                <input
                  type="number"
                  value={formData.seat_count}
                  onChange={(e) => setFormData({ ...formData, seat_count: parseInt(e.target.value) || 4 })}
                  min="1"
                  max="20"
                />
              </div>
              <div className="form-group">
                <label>Vehicle Categories *</label>
                <div className="categories-container">
                  {vehicleTypes.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      className={`category-chip ${formData.vehicle_type_ids.includes(type.id) ? 'selected' : ''}`}
                      onClick={() => handleToggleCategory(type.id)}
                    >
                      {type.name}
                      {formData.vehicle_type_ids.includes(type.id) && (
                        <MaterialCommunityIcons name="check" size={16} color={Colors.white} />
                      )}
                    </button>
                  ))}
                </div>
                {vehicleTypes.length === 0 && (
                  <p className="form-hint">Loading vehicle types...</p>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-button" onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button className="submit-button" onClick={handleSubmit}>
                {editingVehicle ? 'Update' : 'Add'} Vehicle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehiclesScreen;
